import { TabItem } from '@/types/navigation';
import { Sparkles, MessageCircle, Settings } from 'lucide-react';

/**
 * TabManager handles the definition and management of navigation tabs
 */
export class TabManager {
  private static readonly TAB_DEFINITIONS: Omit<TabItem, 'isActive' | 'badge'>[] = [
    {
      id: 'discover',
      path: '/',
      label: 'Discover',
      icon: 'Sparkles',
    },
    {
      id: 'conversations',
      path: '/conversations',
      label: 'My Conversations',
      icon: 'MessageCircle',
    },
    {
      id: 'settings',
      path: '/settings',
      label: 'Settings',
      icon: 'Settings',
    },
  ];

  /**
   * Get all tab definitions
   */
  static getTabDefinitions(): Omit<TabItem, 'isActive' | 'badge'>[] {
    return [...this.TAB_DEFINITIONS];
  }

  /**
   * Get a specific tab definition by ID
   */
  static getTabById(id: string): Omit<TabItem, 'isActive' | 'badge'> | undefined {
    return this.TAB_DEFINITIONS.find(tab => tab.id === id);
  }

  /**
   * Get tab ID by path
   */
  static getTabIdByPath(path: string): string {
    const tab = this.TAB_DEFINITIONS.find(tab => {
      if (tab.path === '/') {
        return path === '/' || path.startsWith('/discover');
      }
      return path.startsWith(tab.path);
    });
    
    return tab?.id || 'discover';
  }

  /**
   * Get path by tab ID
   */
  static getPathByTabId(tabId: string): string {
    const tab = this.TAB_DEFINITIONS.find(tab => tab.id === tabId);
    return tab?.path || '/';
  }

  /**
   * Create complete tab items with active state and badges
   */
  static createTabItems(
    activeTabId: string,
    badges: Record<string, number | string> = {}
  ): TabItem[] {
    return this.TAB_DEFINITIONS.map(tab => ({
      ...tab,
      isActive: tab.id === activeTabId,
      badge: badges[tab.id],
    }));
  }

  /**
   * Validate if a tab ID is valid
   */
  static isValidTabId(tabId: string): boolean {
    return this.TAB_DEFINITIONS.some(tab => tab.id === tabId);
  }

  /**
   * Get the default tab ID
   */
  static getDefaultTabId(): string {
    return 'discover';
  }

  /**
   * Get all valid tab IDs
   */
  static getAllTabIds(): string[] {
    return this.TAB_DEFINITIONS.map(tab => tab.id);
  }

  /**
   * Get Lucide React icon component name for a tab
   */
  static getIconName(tabId: string): string {
    const tab = this.getTabById(tabId);
    return tab?.icon || 'Circle';
  }

  /**
   * Check if a tab should be hidden on certain pages
   */
  static shouldHideOnPath(path: string): boolean {
    // Hide navigation on setup page and individual conversation pages
    const hiddenPaths = ['/setup', '/login', '/register'];
    const hiddenPatterns = [/^\/conversation\/[^/]+$/];
    
    return hiddenPaths.includes(path) || 
           hiddenPatterns.some(pattern => pattern.test(path));
  }

  /**
   * Get tab accessibility label
   */
  static getAccessibilityLabel(tabId: string): string {
    const tab = this.getTabById(tabId);
    if (!tab) return `Navigate to ${tabId}`;
    
    return `Navigate to ${tab.label}`;
  }

  /**
   * Get tab test ID for testing purposes
   */
  static getTestId(tabId: string): string {
    return `nav-${tabId}`;
  }
}

export default TabManager;